// hooks
import {
  type MutationParams,
  useCommonInfiniteQueryWithScroll,
  UseCommonInfiniteQueryWithScrollOptionsType,
  useCommonMutation,
  useCommonQuery
} from 'hooks';

// modules
import type {
  BaseApiResponse,
  CommonQueryOptions,
  ReqSystemList,
  ReqSystemSave,
  ReqSystemUpdate,
  ResultError,
  SystemCode,
  UseGetSystemListInfiniteResult
} from 'modules';

// apis
import { SystemAPI } from '../apis/system.api';

// keys
import { systemKeys } from '../keys/system.key';

// libraries
import { UseMutationResult } from '@tanstack/react-query';

export const systemApi = new SystemAPI();

/** 시스템 목록 조회 쿼리 훅 */
export const useGetSystemList = ({
  data,
  options
}: {
  data: ReqSystemList;
  options?: CommonQueryOptions<BaseApiResponse<unknown, SystemCode>, ResultError, SystemCode[]>;
}) => {
  const queryKeyFactory = () => systemKeys.SYSTEM_ALL;

  const queryFn = async (data: ReqSystemList) => {
    return await systemApi.getSystemList(data);
  };

  return useCommonQuery<
    ReqSystemList,
    BaseApiResponse<unknown, SystemCode>,
    ResultError,
    SystemCode[]
  >({
    data,
    options: {
      ...options,
      select: (data: BaseApiResponse<unknown, SystemCode>) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 시스템 코드 추가 쿼리 훅 */
export const useSaveSystemCode = (): UseMutationResult<
  BaseApiResponse<SystemCode>,
  ResultError,
  MutationParams<BaseApiResponse<SystemCode>, ReqSystemSave>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<SystemCode>, ResultError, ReqSystemSave>({
    mutationKey: systemKeys.SYSTEM_ALL,
    mutationFn: async (data) => await systemApi.saveSystemCode(data),
    queryKeyToInvalidate: systemKeys.SYSTEM_ALL
  });
};

/** 시스템 코드 업데이트 쿼리 훅 */
export const useUpdateSystemCode = (): UseMutationResult<
  BaseApiResponse<SystemCode>,
  ResultError,
  MutationParams<BaseApiResponse<SystemCode>, ReqSystemUpdate>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<SystemCode>, ResultError, ReqSystemUpdate>({
    mutationKey: systemKeys.SYSTEM_ALL,
    mutationFn: async (data) => await systemApi.updateSystemCode(data),
    queryKeyToInvalidate: systemKeys.SYSTEM_ALL
  });
};

/** 시스템 목록 조회 (Public) 쿼리 훅 */
export const useGetPublicSystemList = ({
  data,
  options
}: {
  data: ReqSystemList;
  options?: CommonQueryOptions<BaseApiResponse<unknown, SystemCode>, ResultError, SystemCode>;
}) => {
  const queryKeyFactory = () => systemKeys.SYSTEM_ALL;

  const queryFn = async (data: ReqSystemList) => {
    return await systemApi.getPublicSystemList(data);
  };

  return useCommonQuery<
    ReqSystemList,
    BaseApiResponse<unknown, SystemCode>,
    ResultError,
    SystemCode
  >({
    data,
    options,
    queryKeyFactory,
    queryFn
  });
};

/** 시스템 범위 조회 쿼리 훅 */
export const useGetSystemListByRange = ({
  data,
  options
}: {
  data: { clsCdStart?: number; clsCdLast?: number };
  options?: CommonQueryOptions<BaseApiResponse<unknown, SystemCode>, ResultError, SystemCode>;
}) => {
  const queryKeyFactory = (data: { clsCdStart?: number; clsCdLast?: number }) =>
    systemKeys.GET_SYSTEM_LIST_BY_RANGE(data);

  const queryFn = async (data: { clsCdStart?: number; clsCdLast?: number }) => {
    return await systemApi.getSystemListByRange(data);
  };

  return useCommonQuery<
    { clsCdStart?: number; clsCdLast?: number },
    BaseApiResponse<unknown, SystemCode>,
    ResultError,
    SystemCode
  >({
    data,
    options,
    queryKeyFactory,
    queryFn
  });
};

/** 시스템 세팅 삭제 쿼리 훅 */
export const useDeleteSystemCode = (): UseMutationResult<
  BaseApiResponse<string>,
  ResultError,
  MutationParams<BaseApiResponse<string>, string>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<string>, ResultError, string>({
    mutationKey: systemKeys.SYSTEM_ALL,
    mutationFn: async (data) => await systemApi.deleteSystemCode(data),
    queryKeyToInvalidate: systemKeys.SYSTEM_ALL
  });
};

/** 시스템 목록 조회 쿼리 훅 - 무한스크롤 */
export const useGetSystemListInfinite = ({
  data,
  options
}: {
  data: ReqSystemList;
  options?: UseCommonInfiniteQueryWithScrollOptionsType<
    BaseApiResponse<unknown, SystemCode>,
    ResultError
  >;
}): UseGetSystemListInfiniteResult => {
  const queryKeyFactory = (data: ReqSystemList) => systemKeys.GET_SYSTEM_LIST(data);

  const queryFn = async (data: ReqSystemList, { page, size }: { page: number; size: number }) => {
    return await systemApi.getSystemList({ ...data, page, size });
  };

  return useCommonInfiniteQueryWithScroll({
    data,
    options,
    queryKeyFactory,
    queryFn
  });
};

/** 시스템 목록 조회 쿼리 훅 - 무한스크롤 */
export const useGetSafetySystemListInfinite = ({
  data,
  options
}: {
  data: ReqSystemList;
  options?: UseCommonInfiniteQueryWithScrollOptionsType<
    BaseApiResponse<unknown, SystemCode>,
    ResultError
  >;
}): UseGetSystemListInfiniteResult => {
  const queryKeyFactory = (data: ReqSystemList) => systemKeys.GET_SYSTEM_LIST(data);

  const queryFn = async (data: ReqSystemList, { page, size }: { page: number; size: number }) => {
    return await systemApi.getSafetySystemList({ ...data, page, size });
  };

  return useCommonInfiniteQueryWithScroll({
    data,
    options,
    queryKeyFactory,
    queryFn
  });
};

/** 안전교육 시스템 코드 추가 쿼리 훅 */
export const useSafetySaveSystemCode = (): UseMutationResult<
  BaseApiResponse<SystemCode>,
  ResultError,
  MutationParams<BaseApiResponse<SystemCode>, ReqSystemSave>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<SystemCode>, ResultError, ReqSystemSave>({
    mutationKey: systemKeys.SYSTEM_ALL,
    mutationFn: async (data) => await systemApi.saveSafetySystemCode(data),
    queryKeyToInvalidate: systemKeys.SYSTEM_ALL
  });
};

/** 안전교육 시스템 코드 업데이트 쿼리 훅 */
export const useSafetyUpdateSystemCode = (): UseMutationResult<
  BaseApiResponse<SystemCode>,
  ResultError,
  MutationParams<BaseApiResponse<SystemCode>, ReqSystemUpdate>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<SystemCode>, ResultError, ReqSystemUpdate>({
    mutationKey: systemKeys.SYSTEM_ALL,
    mutationFn: async (data) => await systemApi.updateSafetySystemCode(data),
    queryKeyToInvalidate: systemKeys.SYSTEM_ALL
  });
};

/** 안전교육 시스템 세팅 삭제 쿼리 훅 */
export const useSafetyDeleteSystemCode = (): UseMutationResult<
  BaseApiResponse<string>,
  ResultError,
  MutationParams<BaseApiResponse<string>, string>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<string>, ResultError, string>({
    mutationKey: systemKeys.SYSTEM_ALL,
    mutationFn: async (data) => await systemApi.deleteSafetySystemCode(data),
    queryKeyToInvalidate: systemKeys.SYSTEM_ALL
  });
};
